import { DEFAULT_LOCALE, LOCALES } from "./localization.constants";
import { useLocale } from "./use-locale";

export function isLocalizableHref(href: string) {
    // only paths starting with a single slash to prevent localizing URLs
    // starts with a slash, followed by the end of the string or anything that's not a slash, excludes /blog/* urls
    return /^(?!\/\/)\/(blog$|$|(?!blog\/).+)/.test(href);
}

export function isAlreadyLocalizedHref(href: string) {
    return (
        LOCALES.some((locale) => href.startsWith(`/${locale}/`)) ||
        LOCALES.some((locale) => href === `/${locale}`)
    );
}

export interface UseLocalizedHrefOptions {
    supportedLocales?: string[];
}

export function useLocalizedHref(
    href: string,
    opts: UseLocalizedHrefOptions = {}
) {
    const { supportedLocales } = opts;
    const locale = useLocale();

    return getLocalizedHref({ locale, href, supportedLocales });
}

export interface GetLocaledHrefParams {
    locale: string;
    href: string;
    supportedLocales?: string[];
}

export function getLocalizedHref({
    locale,
    href,
    supportedLocales
}: GetLocaledHrefParams) {
    if (
        locale === DEFAULT_LOCALE ||
        isAlreadyLocalizedHref(href) ||
        !isLocalizableHref(href)
    ) {
        return href;
    }

    if (supportedLocales) {
        return supportedLocales.includes(locale) ? `/${locale}${href}` : href;
    }

    return `/${locale}${href}`;
}

/**
 * Origins stripped to a relative path so links resolve against the current
 * environment. Only applies to known internal origins (atlassian.com, jira,
 * id). External URLs are returned unchanged.
 *
 * Practice: internal links → relative path (same site); external links → full
 * URL unchanged.
 * Additional hosts can be added as use cases arise.
 */
const LOCALIZABLE_ORIGIN_REGEX =
    /^https?:\/\/((www\.)?atlassian\.com|jira\.atlassian\.com|id\.atlassian\.com)/;

type Product = "wac" | "jira" | "id";
type Env = "prod" | "dev" | "stg";

const PRODUCT_ENV_BASE_URLS: Record<Product, Record<Env, string>> = {
    wac: {
        prod: "https://www.atlassian.com",
        dev: "https://wac.dev.internal.atlassian.com",
        stg: "https://wac.stg.internal.atlassian.com"
    },
    jira: {
        prod: "https://jira.atlassian.com",
        dev: "https://jira.dev.internal.atlassian.com",
        stg: "https://jira.stg.internal.atlassian.com"
    },
    id: {
        prod: "https://id.atlassian.com",
        dev: "https://id.stg.internal.atlassian.com",
        stg: "https://id.stg.internal.atlassian.com"
    }
};

function getSourceProductAndPath(
    href: string
): { product: Product; path: string } | null {
    const match = LOCALIZABLE_ORIGIN_REGEX.exec(href);
    if (!match || match[1] === undefined) return null;
    const path = href.replace(LOCALIZABLE_ORIGIN_REGEX, "") || "/";
    const host = match[1];
    if (/jira\.atlassian\.com/.test(host)) return { product: "jira", path };
    if (/id\.atlassian\.com/.test(host)) return { product: "id", path };
    return { product: "wac", path };
}

function parseOriginProductAndEnv(
    origin: string
): { product: Product; env: Env } | null {
    let host: string;
    try {
        host = new URL(origin).hostname;
    } catch {
        return null;
    }
    switch (host) {
        case "atlassian.com":
        case "www.atlassian.com":
            return { product: "wac", env: "prod" };
        case "wac.dev.internal.atlassian.com":
            return { product: "wac", env: "dev" };
        case "wac.stg.internal.atlassian.com":
            return { product: "wac", env: "stg" };
        case "jira.atlassian.com":
            return { product: "jira", env: "prod" };
        case "jira.dev.internal.atlassian.com":
            return { product: "jira", env: "dev" };
        case "jira.stg.internal.atlassian.com":
            return { product: "jira", env: "stg" };
        case "id.atlassian.com":
            return { product: "id", env: "prod" };
        case "id.stg.internal.atlassian.com":
        case "id.dev.internal.atlassian.com":
            return { product: "id", env: "stg" };
        default:
            return null;
    }
}

export function absoluteToRelativeHref(href: string): string {
    // External (non-matching) URLs: return unchanged (full URL).
    if (!LOCALIZABLE_ORIGIN_REGEX.test(href)) {
        return href;
    }
    const parsed = getSourceProductAndPath(href);
    if (!parsed) return href;

    const origin =
        typeof window !== "undefined" ? window.location?.origin : undefined;

    if (!origin) {
        // SSR/test: no origin. Strip to path for wac/jira so snapshots get
        // relative hrefs; keep full URL for id so login/signin links work.
        if (parsed.product === "id") return href;
        return parsed.path;
    }

    const current = parseOriginProductAndEnv(origin);
    if (!current) {
        if (parsed.product === "id") return href;
        return parsed.path;
    }

    if (parsed.product === current.product) {
        return parsed.path;
    }

    const base = PRODUCT_ENV_BASE_URLS[parsed.product][current.env];
    return `${base}${parsed.path.startsWith("/") ? "" : "/"}${parsed.path}`;
}

function getTrelloPath(trelloRoute: string) {
    if (trelloRoute === "/") {
        return "";
    }
    return trelloRoute;
}

export function useLocalizedHrefTrello(trelloRoute: string) {
    const trelloBaseUrl = "https://trello.com";
    const locale = useLocale();
    const trelloPath = getTrelloPath(trelloRoute);
    if (locale === DEFAULT_LOCALE) {
        return `${trelloBaseUrl}${trelloPath}`;
    }
    return `${trelloBaseUrl}/${locale}${trelloPath}`;
}
